/**
 * Phase 7.2 — Admin page to reclassify needs_user_review movements.
 *
 * Layout:
 *   <Breadcrumb>
 *   <Header> live count + "Crear canonical_type" button
 *   <Filters> canonical_type / source / search / date range (via useFilters)
 *   <BulkToolbar> shown when ≥1 row selected — reclassify-as select + reason + apply
 *   <Table> rows w/ checkbox + click-row → MovementDetailModal
 *   <Pagination> 25 / 50 / 100
 *   <CreateCanonicalTypeModal>
 *   <ConfirmDialog> before bulk apply
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Warehouse,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import inventoryReview from "../api/inventoryReview";
import { formatApiError } from "../api/client";
import MovementDetailModal from "../components/inventory/MovementDetailModal";
import CreateCanonicalTypeModal from "../components/inventory/CreateCanonicalTypeModal";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const CREATE_NEW_VALUE = "__create_new__";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("es-MX", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function InventoryReview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Filters ─────────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filterType, setFilterType] = useState("__all__");
  const [filterSource, setFilterSource] = useState("__all__");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // ── Data ────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [canonicalTypes, setCanonicalTypes] = useState([]);

  // ── Selection + bulk reclassify ─────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkNewType, setBulkNewType] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);

  // ── Modals ──────────────────────────────────────────────────────────
  const [detailId, setDetailId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // ── Effects ─────────────────────────────────────────────────────────
  // Fetch canonical types on mount + when refreshed.
  const loadCanonicalTypes = useCallback(() => {
    inventoryReview
      .listCanonicalTypes()
      .then(({ data }) => setCanonicalTypes(data.items || []))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => {
    loadCanonicalTypes();
  }, [loadCanonicalTypes]);

  // Fetch needs-review list.
  useEffect(() => {
    setLoading(true);
    inventoryReview
      .listNeedsReview({
        page,
        page_size: pageSize,
        canonical_type: filterType === "__all__" ? null : filterType,
        source: filterSource === "__all__" ? null : filterSource,
        search: search || null,
      })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Clear selection that no longer matches current page.
        setSelectedIds((prev) => {
          const visible = new Set((data.items || []).map((it) => it.id));
          const next = new Set();
          prev.forEach((id) => {
            if (visible.has(id)) next.add(id);
          });
          return next;
        });
      })
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [page, pageSize, filterType, filterSource, search, refreshKey]);

  // Search debounce.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // ── Helpers ─────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const allOnPageSelected =
    items.length > 0 && items.every((it) => selectedIds.has(it.id));
  const someOnPageSelected =
    !allOnPageSelected && items.some((it) => selectedIds.has(it.id));

  const toggleAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        items.forEach((it) => next.delete(it.id));
      } else {
        items.forEach((it) => next.add(it.id));
      }
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Unique sources for the filter dropdown (derived from current data).
  const sourceOptions = useMemo(() => {
    const set = new Set();
    items.forEach((it) => it.source && set.add(it.source));
    // Always-shown sentinels even if current page has no representative.
    ["GENDETAILED_PROD", "IC385DETAIL", "IC385P2DETAIL"].forEach((s) =>
      set.add(s),
    );
    return Array.from(set).sort();
  }, [items]);

  // Bulk type select handler (with `Crear nuevo` sentinel).
  const onBulkTypeChange = (val) => {
    if (val === CREATE_NEW_VALUE) {
      setCreateOpen(true);
      return;
    }
    setBulkNewType(val);
  };

  const beginBulk = () => {
    if (!bulkNewType || selectedIds.size === 0) {
      toast.error(
        t("inventory.review.bulkMissing", {
          defaultValue:
            "Selecciona al menos un movimiento y un tipo destino.",
        }),
      );
      return;
    }
    setConfirmOpen(true);
  };

  const runBulk = async () => {
    setBulkApplying(true);
    try {
      const { data } = await inventoryReview.reclassify({
        movement_ids: Array.from(selectedIds),
        new_canonical_type: bulkNewType,
        reason: bulkReason,
      });
      toast.success(
        t("inventory.review.bulkOk", {
          defaultValue: `${data.updated} movimientos reclasificados`,
          updated: data.updated,
        }),
      );
      setSelectedIds(new Set());
      setBulkReason("");
      setBulkNewType("");
      setConfirmOpen(false);
      setRefreshKey((k) => k + 1);
      loadCanonicalTypes(); // used_count is denormalized; refresh
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkApplying(false);
    }
  };

  const onCanonicalTypeCreated = (newType) => {
    loadCanonicalTypes();
    setBulkNewType(newType.name);
    toast.success(
      t("inventory.review.bulkReadyAfterCreate", {
        defaultValue: `Tipo "${newType.name}" listo para usarse`,
        name: newType.name,
      }),
    );
  };

  const onSingleReclassified = () => {
    setRefreshKey((k) => k + 1);
    loadCanonicalTypes();
  };

  const fromRow = items.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const toRow = items.length === 0 ? 0 : (page - 1) * pageSize + items.length;

  return (
    <div className="space-y-5" data-testid="inventory-review-page">
      {/* Breadcrumb */}
      <Breadcrumb data-testid="review-breadcrumb">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/inventory">
                <Warehouse className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                {t("nav.inventory", { defaultValue: "Inventario" })}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>
              {t("inventory.review.breadcrumb", {
                defaultValue: "Revisar movimientos pendientes",
              })}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("inventory.review.title", {
              defaultValue: "Revisar movimientos pendientes",
            })}
          </h1>
          <p
            className="text-sm text-muted-foreground mt-0.5"
            data-testid="review-live-count"
          >
            {t("inventory.review.liveCount", {
              defaultValue: `${total.toLocaleString("en-US")} movimientos requieren revisión`,
              total: total.toLocaleString("en-US"),
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey((k) => k + 1)}
            data-testid="review-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            {t("common.refresh", { defaultValue: "Refrescar" })}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCreateOpen(true)}
            data-testid="review-create-type-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t("inventory.review.createTypeBtn", {
              defaultValue: "Crear canonical_type",
            })}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/inventory")}
            data-testid="review-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("common.back", { defaultValue: "Volver" })}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="review-filters">
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-4 gap-2.5">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              {t("inventory.review.filterType", {
                defaultValue: "Canonical actual",
              })}
            </label>
            <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-canonical-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos</SelectItem>
                <SelectItem value="desconocido">desconocido</SelectItem>
                <SelectItem value="devolucion">devolucion</SelectItem>
                {canonicalTypes
                  .filter((c) => c.name !== "devolucion")
                  .map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              {t("inventory.review.filterSource", { defaultValue: "Fuente" })}
            </label>
            <Select value={filterSource} onValueChange={(v) => { setFilterSource(v); setPage(1); }}>
              <SelectTrigger data-testid="filter-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas</SelectItem>
                {sourceOptions.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">
              {t("inventory.review.filterSearch", {
                defaultValue: "Buscar SKU o folio",
              })}
            </label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                placeholder="SKU01-123 / FOLIO-456"
                className="pl-8"
                data-testid="filter-search"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk toolbar */}
      {selectedIds.size > 0 ? (
        <Card
          className="border-primary/40 bg-primary/5"
          data-testid="review-bulk-toolbar"
        >
          <CardContent className="p-3 grid grid-cols-1 md:grid-cols-[auto,1fr,2fr,auto] gap-2.5 items-end">
            <div className="text-sm">
              <Badge
                variant="default"
                className="font-mono"
                data-testid="bulk-count-badge"
              >
                {selectedIds.size}
              </Badge>
              <span className="ml-2 text-muted-foreground text-xs">
                {t("inventory.review.bulkSelected", {
                  defaultValue: "seleccionados",
                })}
              </span>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                {t("inventory.review.bulkAs", {
                  defaultValue: "Reclasificar como...",
                })}
              </label>
              <Select value={bulkNewType} onValueChange={onBulkTypeChange}>
                <SelectTrigger data-testid="bulk-new-type">
                  <SelectValue
                    placeholder={t("inventory.review.bulkAsPlaceholder", {
                      defaultValue: "Elegir tipo",
                    })}
                  />
                </SelectTrigger>
                <SelectContent>
                  {canonicalTypes.map((ct) => (
                    <SelectItem
                      key={ct.name}
                      value={ct.name}
                      data-testid={`bulk-type-opt-${ct.name}`}
                    >
                      <span className="font-mono text-xs">{ct.name}</span>
                      <span className="ml-1.5 text-muted-foreground text-xs">
                        · {ct.display_name}
                      </span>
                      {ct.is_system ? (
                        <Badge
                          variant="outline"
                          className="ml-1.5 text-[10px] py-0 px-1"
                        >
                          sys
                        </Badge>
                      ) : null}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value={CREATE_NEW_VALUE}
                    data-testid="bulk-type-opt-create-new"
                  >
                    <Sparkles className="h-3 w-3 inline mr-1" />
                    <span className="italic">
                      {t("inventory.review.bulkCreateNew", {
                        defaultValue: "Crear nuevo tipo...",
                      })}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                {t("inventory.review.bulkReason", {
                  defaultValue: "Motivo (opcional, máx 200 chars)",
                })}
              </label>
              <Textarea
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value.slice(0, 200))}
                rows={1}
                placeholder="ej. devoluciones del 06-mar..."
                data-testid="bulk-reason"
                className="resize-none min-h-9 h-9 py-1.5"
              />
            </div>
            <Button
              onClick={beginBulk}
              disabled={!bulkNewType || bulkApplying}
              data-testid="bulk-apply-btn"
            >
              {bulkApplying ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              {t("inventory.review.bulkApply", {
                defaultValue: `Aplicar a ${selectedIds.size}`,
                count: selectedIds.size,
              })}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="review-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        allOnPageSelected
                          ? true
                          : someOnPageSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={toggleAllOnPage}
                      data-testid="row-select-all"
                      aria-label="Seleccionar todos en página"
                    />
                  </TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Folio</TableHead>
                  <TableHead>Canonical</TableHead>
                  <TableHead>Raw type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Hist</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <TableRow key={`s-${idx}`}>
                      {Array.from({ length: 9 }).map((__, c) => (
                        <TableCell key={c}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center text-sm text-muted-foreground py-8"
                      data-testid="review-empty"
                    >
                      {t("inventory.review.empty", {
                        defaultValue: "No hay movimientos pendientes con estos filtros.",
                      })}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((it) => {
                    const checked = selectedIds.has(it.id);
                    return (
                      <TableRow
                        key={it.id}
                        data-state={checked ? "selected" : undefined}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => setDetailId(it.id)}
                        data-testid={`review-row-${it.id}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleOne(it.id)}
                            data-testid={`row-cb-${it.id}`}
                            aria-label={`Seleccionar ${it.sku}`}
                          />
                        </TableCell>
                        <TableCell className="text-xs font-mono whitespace-nowrap">
                          {fmtDate(it.timestamp)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {it.sku}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {it.qty}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {it.folio || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {it.canonical_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {it.raw_transaction_type || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {it.source}
                        </TableCell>
                        <TableCell className="text-right">
                          {it.history_count > 0 ? (
                            <Badge
                              variant="outline"
                              className="text-[10px] py-0 px-1"
                            >
                              {it.history_count}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground/50 text-xs">
                              —
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination footer */}
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-border/60 flex-wrap gap-2">
            <div className="text-xs text-muted-foreground">
              {fromRow}-{toRow} de {total.toLocaleString("en-US")}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
              >
                <SelectTrigger className="w-20 h-8" data-testid="page-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="page-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="page-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <MovementDetailModal
        open={!!detailId}
        onOpenChange={(v) => {
          if (!v) setDetailId(null);
        }}
        movementId={detailId}
        canonicalTypes={canonicalTypes}
        onReclassified={onSingleReclassified}
      />
      <CreateCanonicalTypeModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={onCanonicalTypeCreated}
      />

      {/* Confirm bulk */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("inventory.review.confirmTitle", {
                defaultValue: "Confirmar reclasificación",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("inventory.review.confirmBody", {
                defaultValue: `Se reclasificarán ${selectedIds.size} movimientos a "${bulkNewType}". Esta acción se registra en el historial de cada movimiento y recalcula stock_delta.`,
                count: selectedIds.size,
                type: bulkNewType,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={bulkApplying}
              data-testid="bulk-confirm-cancel"
            >
              {t("common.cancel", { defaultValue: "Cancelar" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulk}
              disabled={bulkApplying}
              data-testid="bulk-confirm-apply"
            >
              {bulkApplying ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              {t("inventory.review.confirmApply", {
                defaultValue: "Sí, aplicar",
              })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
